// web/src/pages/ReallocationPage.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { PortfolioTriageTable, type PortfolioRow } from "../components/PortfolioTriageTable";
import { EfficiencyScatter, type ScatterRow } from "../components/EfficiencyScatter";
import { RecommendationFeed } from "../components/RecommendationFeed";
import type { RecRow } from "../components/RecommendationFeed";

const ALLOWED_WINDOWS = [7, 14, 30] as const;
type WindowDays = typeof ALLOWED_WINDOWS[number];

function parseWindow(raw: string | null): WindowDays {
  const n = Number(raw);
  return (ALLOWED_WINDOWS as readonly number[]).includes(n) ? (n as WindowDays) : 14;
}

interface PortfolioResponse { accounts: PortfolioRow[] }
interface EaResponse {
  account: { account_id: string; display_name: string; currency: string };
  recs: RecRow[];
  adsets: ScatterRow[];
}

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`fetch failed ${r.status}`);
  return await r.json() as T;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, {
    method: "POST", credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`post failed ${r.status}`);
  return await r.json() as T;
}

async function del(url: string): Promise<void> {
  const r = await fetch(url, { method: "DELETE", credentials: "include" });
  if (!r.ok && r.status !== 204) throw new Error(`delete failed ${r.status}`);
}

export function ReallocationPage() {
  const { accountId } = useParams<{ accountId?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const windowDays = parseWindow(searchParams.get("window"));
  const [showSuppressed, setShowSuppressed] = useState(false);

  const [portfolio, setPortfolio] = useState<PortfolioRow[] | null>(null);
  const [eaData, setEaData] = useState<EaResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshPortfolio = useCallback(async () => {
    try { setPortfolio((await getJson<PortfolioResponse>(`/api/reallocation/portfolio?window=${windowDays}`)).accounts); }
    catch (e) { setError(String(e)); }
  }, [windowDays]);

  const refreshEa = useCallback(async () => {
    if (!accountId) { setEaData(null); return; }
    try {
      const suffix = showSuppressed ? "&showSuppressed=1" : "";
      setEaData(await getJson<EaResponse>(`/api/reallocation/ea/${encodeURIComponent(accountId)}?window=${windowDays}${suffix}`));
    } catch (e) { setError(String(e)); }
  }, [accountId, windowDays, showSuppressed]);

  useEffect(() => { void refreshPortfolio(); }, [refreshPortfolio]);
  useEffect(() => { void refreshEa(); }, [refreshEa]);

  const handleSelect = useCallback((id: string) => {
    navigate(`/dashboard/reallocation/${encodeURIComponent(id)}?window=${windowDays}`);
  }, [navigate, windowDays]);

  const handleWindow = useCallback((w: WindowDays) => {
    const next = new URLSearchParams(searchParams);
    next.set("window", String(w));
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const handleAction = useCallback(async (rec_id: string, input: { status: "snoozed" | "done" | "dismissed"; snooze_days?: 7 | 14 }) => {
    try {
      await postJson("/api/reallocation/actions", { rec_id, ...input });
      await Promise.all([refreshPortfolio(), refreshEa()]);
    } catch (e) { setError(String(e)); }
  }, [refreshPortfolio, refreshEa]);

  const handleUnsnooze = useCallback(async (rec_id: string) => {
    if (!eaData) return;
    const rec = eaData.recs.find((r) => r.rec_id === rec_id);
    if (!rec?.action_id) return;
    try {
      await del(`/api/reallocation/actions/${encodeURIComponent(rec.action_id)}`);
      await Promise.all([refreshPortfolio(), refreshEa()]);
    } catch (e) { setError(String(e)); }
  }, [eaData, refreshPortfolio, refreshEa]);

  const suppressedCount = useMemo(() => {
    if (!eaData) return 0;
    return eaData.recs.filter((r) => r.suppressed_until !== null || r.dismissed_at !== null).length;
  }, [eaData]);

  return (
    <Layout>
      <div style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
          <h1 style={{ fontSize: 18, margin: 0, color: "#cdd3dc" }}>Reallocation</h1>
          <div style={{ display: "flex", gap: 6, fontSize: 12 }}>
            {([7, 14, 30] as const).map((w) => (
              <button key={w} type="button" onClick={() => handleWindow(w)}
                style={{ fontWeight: w === windowDays ? 700 : 400 }}>
                {w}d
              </button>
            ))}
          </div>
        </div>

        {error && <div style={{ color: "#d96057", fontSize: 12, marginBottom: 8 }}>{error}</div>}

        {portfolio === null ? (
          <div style={{ color: "#7a808c" }}>Loading portfolio…</div>
        ) : (
          <PortfolioTriageTable
            accounts={portfolio}
            selectedAccountId={accountId ?? null}
            onSelect={handleSelect}
            windowDays={windowDays}
          />
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1.4fr", gap: 0, marginTop: 16, borderTop: "1px solid #232733" }}>
          <div style={{ padding: "16px 20px 16px 0", borderRight: "1px solid #232733" }}>
            <div style={{ fontSize: 11, color: "#7a808c", textTransform: "uppercase", marginBottom: 8 }}>
              Efficiency × Spend
            </div>
            {!accountId ? (
              <div style={{ color: "#7a808c", fontSize: 12 }}>Select an end advertiser.</div>
            ) : eaData === null ? (
              <div style={{ color: "#7a808c", fontSize: 12 }}>Loading…</div>
            ) : (
              <EfficiencyScatter rows={eaData.adsets} />
            )}
          </div>
          <div style={{ padding: "16px 0 16px 20px" }}>
            {!accountId ? (
              <div style={{ color: "#7a808c", fontSize: 12 }}>Select an end advertiser to see recommendations.</div>
            ) : eaData === null ? (
              <div style={{ color: "#7a808c", fontSize: 12 }}>Loading recommendations…</div>
            ) : (
              <RecommendationFeed
                recs={eaData.recs}
                onAction={handleAction}
                onUnsnooze={handleUnsnooze}
                showSuppressed={showSuppressed}
                onToggleSuppressed={() => setShowSuppressed((v) => !v)}
                suppressedCount={suppressedCount}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
